import {
  HALF_PI,
  Mat,
  PI,
  PI2,
  StateNode,
  Vec,
  areAnglesCompatible,
  compact,
  isAccelKey,
  isShapeId,
  kickoutOccludedShapes
} from "@tldraw/editor";
import { getEnclosedShapeIds } from "../../../shapes/frame/FrameShapeTool.mjs";
import { batchMeasureGeoLabels, setBatchLabelSizeCache } from "../../../shapes/geo/GeoShapeUtil.mjs";
class Resizing extends StateNode {
  static id = "resizing";
  static trackPerformance = true;
  info = {};
  markId = "";
  // A switch to detect when the user is holding ctrl
  didHoldCommand = false;
  // we transition into the resizing state from the geo pointing state, which starts with a shape of size w: 1, h: 1,
  // so if the user drags x: +50, y: +50 after mouseDown, the shape will be w: 51, h: 51, which is too many pixels, alas
  // so we allow passing a further offset into this state to negate such issues
  creationCursorOffset = { x: 0, y: 0 };
  snapshot = {};
  onEnter(info) {
    const { isCreating = false, creatingMarkId, creationCursorOffset = { x: 0, y: 0 } } = info;
    this.info = info;
    this.didHoldCommand = false;
    if (typeof info.onInteractionEnd === "string") {
      this.parent.setCurrentToolIdMask(info.onInteractionEnd);
    }
    this.creationCursorOffset = creationCursorOffset;
    try {
      this.snapshot = this._createSnapshot();
    } catch (e) {
      console.error(e);
      this.cancel();
      return;
    }
    this.markId = "";
    if (isCreating) {
      if (creatingMarkId) {
        this.markId = creatingMarkId;
      } else {
        const markId = this.editor.getMarkIdMatching(
          `creating:${this.editor.getOnlySelectedShapeId()}`
        );
        if (markId) {
          this.markId = markId;
        }
      }
    } else {
      this.markId = this.editor.markHistoryStoppingPoint("starting resizing");
    }
    if (isCreating) {
      this.editor.setCursor({ type: "cross", rotation: 0 });
    }
    this.handleResizeStart();
    this.updateShapes();
  }
  onTick({ elapsed }) {
    const { editor } = this;
    if (!editor.inputs.getIsDragging() || editor.inputs.getIsPanning()) return;
    editor.edgeScrollManager.updateEdgeScrolling(elapsed);
  }
  onPointerMove() {
    this.updateShapes();
  }
  onKeyDown() {
    this.updateShapes();
  }
  onKeyUp() {
    this.updateShapes();
  }
  onPointerUp() {
    this.complete();
  }
  onComplete() {
    this.complete();
  }
  onCancel() {
    this.cancel();
  }
  cancel() {
    const { shapeSnapshots } = this.snapshot;
    shapeSnapshots.forEach(({ shape }) => {
      const current = this.editor.getShape(shape.id);
      if (current) {
        const util = this.editor.getShapeUtil(shape);
        util.onResizeCancel?.(shape, current);
      }
    });
    this.editor.bailToMark(this.markId);
    const { onInteractionEnd } = this.info;
    if (onInteractionEnd) {
      if (typeof onInteractionEnd === "string") {
        this.editor.setCurrentTool(onInteractionEnd, {});
      } else {
        onInteractionEnd();
      }
      return;
    }
    this.parent.transition("idle");
  }
  complete() {
    kickoutOccludedShapes(this.editor, this.snapshot.selectedShapeIds);
    this.handleResizeEnd();
    if (this.info.isCreating && this.info.onCreate) {
      this.info.onCreate?.(this.editor.getOnlySelectedShape());
      return;
    }
    const { onInteractionEnd } = this.info;
    if (onInteractionEnd) {
      if (typeof onInteractionEnd === "string") {
        if (this.editor.getInstanceState().isToolLocked) {
          this.editor.setCurrentTool(onInteractionEnd, {});
          return;
        }
      } else {
        onInteractionEnd();
        return;
      }
    }
    this.parent.transition("idle");
  }
  handleResizeStart() {
    const { shapeSnapshots } = this.snapshot;
    const changes = [];
    shapeSnapshots.forEach(({ shape }) => {
      const util = this.editor.getShapeUtil(shape);
      const change = util.onResizeStart?.(shape);
      if (change) {
        changes.push(change);
      }
    });
    if (changes.length > 0) {
      this.editor.updateShapes(changes);
    }
  }
  handleResizeEnd() {
    const { shapeSnapshots } = this.snapshot;
    const changes = [];
    shapeSnapshots.forEach(({ shape }) => {
      const current = this.editor.getShape(shape.id);
      const util = this.editor.getShapeUtil(shape);
      const change = util.onResizeEnd?.(shape, current);
      if (change) {
        changes.push(change);
      }
    });
    if (changes.length > 0) {
      this.editor.updateShapes(changes);
    }
  }
  updateShapes() {
    const {
      editor,
      info,
      snapshot: {
        frames,
        shapeSnapshots,
        selectionBounds,
        cursorHandleOffset,
        selectedShapeIds,
        selectionRotation,
        canShapesDeform,
        initialSelectionPageBounds,
        resizeLevels
      }
    } = this;
    const altKey = editor.inputs.getAltKey();
    const shiftKey = editor.inputs.getShiftKey();
    let isAspectRatioLocked = shiftKey || !canShapesDeform;
    if (shapeSnapshots.size === 1) {
      const onlySnapshot = [...shapeSnapshots.values()][0];
      if (editor.isShapeOfType(onlySnapshot.shape, "text")) {
        isAspectRatioLocked = !(info.handle === "left" || info.handle === "right");
      }
    }
    const isHoldingAccel = isAccelKey(editor.inputs);
    const currentPagePoint = editor.inputs.getCurrentPagePoint().clone().sub(cursorHandleOffset).sub(this.creationCursorOffset);
    const originPagePoint = editor.inputs.getOriginPagePoint().clone().sub(cursorHandleOffset);
    if (editor.getInstanceState().isGridMode && !isHoldingAccel) {
      const { gridSize } = editor.getDocumentSettings();
      currentPagePoint.snapToGrid(gridSize);
    }
    const dragHandle = info.handle;
    const scaleOriginHandle = rotateSelectionHandle(dragHandle, Math.PI);
    editor.snaps.clearIndicators();
    const shouldSnap = editor.user.getIsSnapMode() ? !isHoldingAccel : isHoldingAccel;
    if (shouldSnap && selectionRotation % HALF_PI === 0) {
      const { nudge } = editor.snaps.shapeBounds.snapResizeShapes({
        dragDelta: Vec.Sub(currentPagePoint, originPagePoint),
        initialSelectionPageBounds,
        handle: rotateSelectionHandle(dragHandle, selectionRotation),
        isAspectRatioLocked,
        isResizingFromCenter: altKey
      });
      currentPagePoint.add(nudge);
    }
    const scaleOriginPage = Vec.RotWith(
      altKey ? selectionBounds.center : selectionBounds.getHandlePoint(scaleOriginHandle),
      selectionBounds.point,
      selectionRotation
    );
    const distanceFromScaleOriginNow = Vec.Sub(currentPagePoint, scaleOriginPage).rot(
      -selectionRotation
    );
    const distanceFromScaleOriginAtStart = Vec.Sub(originPagePoint, scaleOriginPage).rot(
      -selectionRotation
    );
    const scale = Vec.DivV(distanceFromScaleOriginNow, distanceFromScaleOriginAtStart);
    if (!Number.isFinite(scale.x)) scale.x = 1;
    if (!Number.isFinite(scale.y)) scale.y = 1;
    const isXLocked = dragHandle === "top" || dragHandle === "bottom";
    const isYLocked = dragHandle === "left" || dragHandle === "right";
    if (isAspectRatioLocked) {
      if (isYLocked) {
        scale.y = Math.abs(scale.x);
      } else if (isXLocked) {
        scale.x = Math.abs(scale.y);
      } else if (Math.abs(scale.x) > Math.abs(scale.y)) {
        scale.y = Math.abs(scale.x) * (scale.y < 0 ? -1 : 1);
      } else {
        scale.x = Math.abs(scale.y) * (scale.x < 0 ? -1 : 1);
      }
    } else {
      if (isXLocked) {
        scale.x = 1;
      }
      if (isYLocked) {
        scale.y = 1;
      }
    }
    if (!this.info.isCreating) {
      this.updateCursor({
        dragHandle,
        isFlippedX: scale.x < 0,
        isFlippedY: scale.y < 0,
        rotation: selectionRotation
      });
    }
    if (shapeSnapshots.size > 1) {
      batchMeasureGeoLabels(
        this.editor,
        shapeSnapshots,
        scale,
        selectionRotation,
        isAspectRatioLocked
      );
    }
    for (const level of resizeLevels) {
      const changes = [];
      for (const id of level) {
        const snapshot = shapeSnapshots.get(id);
        const change = editor.getResizeShapePartial(id, scale, {
          initialShape: snapshot.shape,
          initialBounds: snapshot.bounds,
          initialPageTransform: snapshot.pageTransform,
          dragHandle,
          mode: selectedShapeIds.length === 1 && id === selectedShapeIds[0] ? "resize_bounds" : "scale_shape",
          scaleOrigin: scaleOriginPage,
          isAspectRatioLocked,
          scaleAxisRotation: selectionRotation,
          skipStartAndEndCallbacks: true
        });
        if (change) changes.push(change);
      }
      if (changes.length > 0) {
        editor.updateShapes(changes);
      }
    }
    if (isHoldingAccel) {
      this.didHoldCommand = true;
      const childChanges = [];
      for (const { id, children } of frames) {
        if (!children.length) continue;
        const initial = shapeSnapshots.get(id).shape;
        const current = this.editor.getShape(id);
        if (!(initial && current)) continue;
        const dx = current.x - initial.x;
        const dy = current.y - initial.y;
        const delta = new Vec(dx, dy).rot(-initial.rotation);
        if (delta.x !== 0 || delta.y !== 0) {
          for (const child of children) {
            childChanges.push({
              id: child.id,
              type: child.type,
              x: child.x - delta.x,
              y: child.y - delta.y
            });
          }
        }
      }
      if (childChanges.length > 0) {
        this.editor.updateShapes(childChanges);
      }
    } else if (this.didHoldCommand) {
      this.didHoldCommand = false;
      const childChanges = [];
      for (const { children } of frames) {
        for (const child of children) {
          childChanges.push({
            id: child.id,
            type: child.type,
            x: child.x,
            y: child.y
          });
        }
      }
      if (childChanges.length > 0) {
        this.editor.updateShapes(childChanges);
      }
    }
    this.updateEnclosureHints();
  }
  // While drag-creating a frame, hint the sibling shapes that would become its children on pointer up
  updateEnclosureHints() {
    if (!this.info.isCreating) return;
    const shape = this.editor.getOnlySelectedShape();
    if (!shape || !this.editor.isShapeOfType(shape, "frame")) return;
    const hintingShapeIds = getEnclosedShapeIds(this.editor, shape);
    const prevHintingShapeIds = this.editor.getHintingShapeIds();
    if (hintingShapeIds.length === prevHintingShapeIds.length && hintingShapeIds.every((id, i) => id === prevHintingShapeIds[i])) {
      return;
    }
    this.editor.setHintingShapes(hintingShapeIds);
  }
  // ---
  updateCursor({
    dragHandle,
    isFlippedX,
    isFlippedY,
    rotation
  }) {
    const prevCursor = this.editor.getInstanceState().cursor;
    let nextCursorType = prevCursor.type;
    switch (dragHandle) {
      case "top_left":
      case "bottom_right": {
        nextCursorType = "nwse-resize";
        if (isFlippedX !== isFlippedY) {
          nextCursorType = "nesw-resize";
        }
        break;
      }
      case "top_right":
      case "bottom_left": {
        nextCursorType = "nesw-resize";
        if (isFlippedX !== isFlippedY) {
          nextCursorType = "nwse-resize";
        }
        break;
      }
    }
    if (nextCursorType === prevCursor.type && rotation === prevCursor.rotation) return;
    this.editor.setCursor({ type: nextCursorType, rotation });
  }
  onExit() {
    this.parent.setCurrentToolIdMask(void 0);
    this.editor.setCursor({ type: "default", rotation: 0 });
    this.editor.snaps.clearIndicators();
    setBatchLabelSizeCache(this.editor, null);
    if (this.info.isCreating && this.editor.getHintingShapeIds().length > 0) {
      this.editor.setHintingShapes([]);
    }
  }
  _createSnapshot() {
    const { editor } = this;
    const selectedShapeIds = editor.getSelectedShapeIds();
    const selectionRotation = editor.getSelectionRotation();
    const originPagePoint = editor.inputs.getOriginPagePoint();
    const selectionBounds = editor.getSelectionRotatedPageBounds();
    if (!selectionBounds) throw Error("Resizing but nothing is selected");
    const dragHandlePoint = Vec.RotWith(
      selectionBounds.getHandlePoint(this.info.handle),
      selectionBounds.point,
      selectionRotation
    );
    const cursorHandleOffset = Vec.Sub(originPagePoint, dragHandlePoint);
    const shapeSnapshots = /* @__PURE__ */ new Map();
    const frames = [];
    const populateResizingShapes = (shapeId) => {
      const shape = editor.getShape(shapeId);
      if (!shape) return false;
      const util = editor.getShapeUtil(shape);
      if (util.canResize(shape)) {
        const pageTransform = editor.getShapePageTransform(shape);
        shapeSnapshots.set(shape.id, {
          shape,
          bounds: editor.getShapeGeometry(shape).bounds,
          pageTransform,
          pageRotation: Mat.Decompose(pageTransform).rotation,
          isAspectRatioLocked: util.isAspectRatioLocked(shape)
        });
      }
      if (editor.isShapeFrameLike(shape)) {
        frames.push({
          id: shape.id,
          children: compact(
            editor.getSortedChildIdsForParent(shape).map((id) => editor.getShape(id))
          )
        });
      }
      if (!util.canResizeChildren(shape)) return false;
      return void 0;
    };
    selectedShapeIds.forEach((shapeId) => {
      const keepDescending = populateResizingShapes(shapeId);
      if (keepDescending === false) return;
      editor.visitDescendants(shapeId, populateResizingShapes);
    });
    const canShapesDeform = ![...shapeSnapshots.values()].some(
      (shape) => !areAnglesCompatible(shape.pageRotation, selectionRotation) || shape.isAspectRatioLocked
    );
    const resizeLevels = [];
    const levelByShapeId = /* @__PURE__ */ new Map();
    const getLevel = (id) => {
      const cached = levelByShapeId.get(id);
      if (cached !== void 0) return cached;
      let level = 0;
      let parentId = editor.getShape(id)?.parentId;
      while (parentId && isShapeId(parentId)) {
        if (shapeSnapshots.has(parentId)) {
          level = getLevel(parentId) + 1;
          break;
        }
        parentId = editor.getShape(parentId)?.parentId;
      }
      levelByShapeId.set(id, level);
      return level;
    };
    for (const id of shapeSnapshots.keys()) {
      const level = getLevel(id);
      while (resizeLevels.length <= level) resizeLevels.push([]);
      resizeLevels[level].push(id);
    }
    return {
      shapeSnapshots,
      selectionBounds,
      cursorHandleOffset,
      selectionRotation,
      selectedShapeIds,
      canShapesDeform,
      initialSelectionPageBounds: this.editor.getSelectionPageBounds(),
      frames,
      resizeLevels
    };
  }
}
const ORDERED_SELECTION_HANDLES = [
  "top",
  "top_right",
  "right",
  "bottom_right",
  "bottom",
  "bottom_left",
  "left",
  "top_left"
];
function rotateSelectionHandle(handle, rotation) {
  rotation = rotation % PI2;
  const numSteps = Math.round(rotation / (PI / 4));
  const currentIndex = ORDERED_SELECTION_HANDLES.indexOf(handle);
  return ORDERED_SELECTION_HANDLES[(currentIndex + numSteps) % ORDERED_SELECTION_HANDLES.length];
}
export {
  Resizing,
  rotateSelectionHandle
};
//# sourceMappingURL=Resizing.mjs.map
